import React from "react";

/**
 * Staggered Benefit Cards (React + Tailwind)
 * - 6 cards (3 per row on xl, 2 per row on md, 1 on mobile)
 * - "Stair / stagger" layout:
 *    Row 1: card1 higher, card2 mid, card3 lower
 *    Row 2: same pattern
 * - Light, classy, a bit stylistic
 *
 * Tip: If you want MORE stagger, increase the translate-y values.
 */

const CareerBenefits = () => {
  const benefits = [
    {
      title: "Attraktive Provision",
      desc: "Provision auf Verkäufe jeglicher Art.",
      icon: <CoinIcon />,
    },
    {
      title: "Weiterbildung",
      desc: "Regelmäßige Coachings & interne Schulungen halten Dich auf dem neuesten Stand.",
      icon: <CapIcon />,
    },
    {
      title: "Firmenleasing",
      desc: "Du möchtest ein E-Bike oder ein Auto leasen? Kein Problem!",
      icon: <CarIcon />,
    },
    {
      title: "Mentoring",
      desc: "Unser erfahrenes Team begleitet Dich durch jede Phase.",
      icon: <MentorIcon />,
    },
    {
      title: "Teamwork makes the dream work!",
      desc: "Wir verfolgen Ziele und haben Spaß dabei!",
      icon: <TeamIcon />,
    },
    {
      title: "Faire Vergütung & Arbeitszeiten",
      desc: "Wir legen großen Wert auf eine ausgewogene WORK-LIFE-BALANCE.",
      icon: <BalanceIcon />,
    },
  ];

  // Stagger per index (0..5) -> 2 rows * 3 cols


  return (
    <section className="w-full bg-white">
      <div className="mx-auto w-[90%] 2xl:w-[70%] py-14 md:py-20">
        {/* Heading */}
        <div className="max-w-2xl text-center mx-auto">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#cac6c6]  px-3 py-1 text-xs font-semibold text-[#8ec700]">
            Benefits
            <span className="h-1 w-1 rounded-full bg-emerald-600" />
            Dein Plus bei uns
          </p>

          <h2 className="mt-4 h2-mb h2-md h2-lg h2-2xl">
            Unsere Mission: Unternehmen online stärken!
          </h2>

          <p className="mt-3 pb-12 text-slate-600 leading-relaxed">
            UNEVA steht für Teamarbeit, Leidenschaft und Erfolg. <br /> Wir glauben daran, dass gemeinsame Anstrengungen zu <br /> außergewöhnlichen Ergebnissen führen.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 xl:gap-7">
          {benefits.map((b, idx) => (
            <div
              key={b.title}
              className={[
                "group relative rounded-2xl border border-[#cac6c6] bg-white/80 p-6 shadow-sm",
                "transition duration-300 hover:-translate-y-1 hover:shadow-lg",
                "backdrop-blur-sm",
                // stagger on xl
               
              ].join(" ")}
            >
              {/* soft decorative gradient */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/8 via-transparent to-lime-500/10 opacity-0 transition group-hover:opacity-100" />

              {/* top row */}
              <div className="relative flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center text-[#8ec700] ">
                  {b.icon}
                </div>

                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {b.desc}
                  </p>
                </div>
              </div>

              {/* subtle bottom line */}
              <div className="relative mt-5 h-px w-full bg-gradient-to-r from-[#8ec700] via-slate-100 to-transparent" />

              {/* mini footer */}
              <div className="relative mt-4 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">
                  Karriere • Benefits
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-[#cac6c6]">
                  Mehr erfahren <ArrowMini />
                </span>
              </div>
            </div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default CareerBenefits;

/* ---------------- Icons ---------------- */

function ArrowMini() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-slate-500">
      <path
        d="M8 16l8-8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10 8h6v6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CoinIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 21a9 9 0 1 0-9-9 9 9 0 0 0 9 9Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M9.5 10.2c.2-1.2 1.3-2.1 2.8-2.1 1.5 0 2.6.8 2.6 2 0 1.2-1 1.7-2.2 2-1.5.4-3 .8-3 2.5 0 1.4 1.2 2.3 2.9 2.3 1.5 0 2.7-.8 3-2.2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 7v10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CapIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3 2 8l10 5 10-5-10-5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M6 10.5V16c0 1.6 2.7 3 6 3s6-1.4 6-3v-5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M22 8v7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 13l2-6a2 2 0 0 1 2-1h10a2 2 0 0 1 2 1l2 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M5 13h14v6H5v-6Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 19.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
        fill="currentColor"
      />
      <path
        d="M16.5 19.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MentorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M20 21c0-4-3.6-7-8-7s-8 3-8 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M17 6l2 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TeamIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M8 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M2 20c0-3 2.7-5 6-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M22 20c0-3-2.7-5-6-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M8 20c0-3 1.8-6 4-6s4 3 4 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BalanceIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3v18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M5 7h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7 7 4 13h6L7 7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M17 7 14 13h6l-3-6Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M10 21h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
